import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import {
    getUnitListPrice,
    getUnitNetPrice,
    getUnitRegularPrice,
    getUnitRegularPriceWithVat,
} from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import PriceDetails from "@WhiteCap/Components/PriceDetails";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    product: ProductContextModel | CartLineModel;
    currencySymbol?: string;
    label?: string;
    showLabel?: boolean;
    showUnitOfMeasure?: boolean;
    showPack?: boolean;
    showSavings?: boolean;
    showSavingsAmount?: boolean;
    showSavingsPercent?: boolean;
    showInvalidPriceMessage?: boolean;
    showSalePriceLabel?: boolean;
    extendedStyles?: ProductPriceStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    currencySymbol: ownProps.currencySymbol ?? state.context.session.currency?.currencySymbol ?? "",
    canSeePrices: getSettingsCollection(state).productSettings.canSeePrices,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
    isCheckoutReviewAndSubmitPage: getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
    session: getSession(state),
    showStandardPrice: state.context.settings.properties.showStandardPrice === "true",
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ProductPriceStyles {
    wrapper?: any;
    priceLabelText?: TypographyPresentationProps;
    quoteMessageWrapper?: InjectableCss;
    quoteMessage?: RequiresQuoteMessageStyle;
    unitOfMeasure?: UnitOfMeasureStyles;
    productPriceWrapper?: InjectableCss;
    callForPriceIcon?: InjectableCss;
    price?: PriceStyles;
    regularPrice?: RegularPriceStyles;
    invalidPriceText?: TypographyPresentationProps;
    secondaryPriceWrapper?: InjectableCss;
    secondaryPrice?: PriceStyles;
    packWrapper?: InjectableCss;
    packLabelText?: TypographyPresentationProps;
    packDescriptionText?: TypographyPresentationProps;
    savingsMessageText?: TypographyPresentationProps;
    callforPriceText?: InjectableCss;
    callForPriceWrapper?: InjectableCss;
}

interface RequiresQuoteMessageStyle {
    text?: TypographyPresentationProps;
    tooltip?: TooltipPresentationProps;
}

interface PriceStyles {
    priceText?: TypographyPresentationProps;
    signInText?: TypographyPresentationProps;
    errorText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    unitOfMeasure?: UnitOfMeasureStyles;
    /**
     * @deprecated Use the `unitOfMeasure.unitOfMeasureText` property instead.
     */
    unitOfMeasureText?: TypographyPresentationProps;
    qtyPerBaseUnitOfMeasureText?: TypographyPresentationProps;
    vatLabelText?: TypographyPresentationProps;
    pricingContainer?: InjectableCss;
    pricingWrapper?: InjectableCss;
    priceLine?: InjectableCss;
    labelText?: TypographyPresentationProps;
}

interface RegularPriceStyles {
    priceText?: TypographyPresentationProps;
    unitOfMeasure?: UnitOfMeasureStyles;
    labelText?: TypographyPresentationProps;
    regularPricewrapper?: InjectableCss;
    priceLine?: InjectableCss;
}

interface UnitOfMeasureStyles {
    unitOfMeasureText?: TypographyPresentationProps;
}

export const getProductPriceStyles = (isStandardPrice: boolean): ProductPriceStyles => ({
    callforPriceText: {
        css: css`
            font-family: inherit;
            font-size: 12px;
            line-height: 16px;
            padding-left: 10px;
            color: #323232;
        `,
    },
    callForPriceIcon: {
        css: css`
            margin-top: -2px;
        `,
    },
    callForPriceWrapper: {
        css: css`
            border: 1px solid;
            display: grid;
            grid-template-columns: 12px auto;
            border-color: #ffb178;
            border-radius: 8px;
            background-color: white;
            max-width: max-content;
            padding: 8px 10px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css``,
                    css``,
                    css``,
                    css`
                        width: 232px;
                    `,
                    css`
                        width: 232px;
                    `,
                ])}
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
        `,
    },
    priceLabelText: {
        variant: "legend",
    },
    productPriceWrapper: {
        css: css`
            background-color: ${isStandardPrice ? "#FAFAFC" : "#fff"};
            padding: ${isStandardPrice ? "6px 8px" : "6px 0px"};
            position: relative;
            ${wrapInContainerStyles}
        `,
    },
    price: {
        pricingContainer: {
            css: css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 4px;
            `,
        },
        pricingWrapper: {
            css: css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
            `,
        },
        priceLine: {
            css: css`
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 4px;
            `,
        },
        labelText: {
            css: css`
                font-size: 12px;
                font-style: normal;
                font-weight: 400;
                line-height: 16px;
            `,
        },
        priceText: {
            css: css`
                font-size: 16px;
                font-style: normal;
                font-weight: 600;
                line-height: 23px;
            `,
        },

        errorText: { weight: "bold" },
        realTimeText: {
            weight: "bold",
            css: css`
                display: inline-block;
                text-align: left;
                min-width: 25px;

                &::after {
                    overflow: hidden;
                    display: inline-block;
                    vertical-align: bottom;
                    animation: ellipsis steps(4, end) 900ms infinite 1s;
                    content: "\\2026";
                    width: 0;
                }

                @keyframes ellipsis {
                    to {
                        width: 1.25em;
                    }
                }
            `,
        },
        unitOfMeasure: {
            unitOfMeasureText: {
                css: css`
                    white-space: nowrap;
                    font-size: 12px;
                    font-style: normal;
                    font-weight: 400;
                    line-height: 16px;
                `,
            },
        },
        vatLabelText: {
            css: css`
                font-size: 12px;
            `,
        },
    },
    regularPrice: {
        regularPricewrapper: {
            css: css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
            `,
        },
        labelText: {
            css: css`
                font-size: 12px;
                font-weight: 400;
                line-height: 16px;
            `,
        },
        priceLine: {
            css: css`
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 4px;
            `,
        },
        priceText: {
            css: css`
                font-size: 12px;
                font-style: normal;
                font-weight: 500;
                line-height: 23px;
            `,
        },
        unitOfMeasure: {
            unitOfMeasureText: {
                css: css`
                    display: inline;
                    white-space: nowrap;
                    font-size: 10px;
                    font-style: normal;
                    font-weight: 400;
                    line-height: 16px;
                `,
            },
        },
    },
    invalidPriceText: {
        color: "danger",
    },
    secondaryPriceWrapper: {
        css: css`
            margin-top: 5px;
            ${wrapInContainerStyles}
        `,
    },
    secondaryPrice: {
        priceText: { weight: "bold" },
        errorText: { weight: "bold" },
        realTimeText: {
            weight: "bold",
            css: css`
                display: inline-block;
                text-align: left;
                min-width: 25px;

                &::after {
                    overflow: hidden;
                    display: inline-block;
                    vertical-align: bottom;
                    animation: ellipsis steps(4, end) 900ms infinite 1s;
                    content: "\\2026";
                    width: 0;
                }

                @keyframes ellipsis {
                    to {
                        width: 1.25em;
                    }
                }
            `,
        },
        unitOfMeasure: {
            unitOfMeasureText: {
                css: css`
                    white-space: nowrap;
                `,
            },
        },
        vatLabelText: {
            css: css`
                font-size: 12px;
            `,
        },
    },
    packWrapper: {
        css: css`
            margin-bottom: 8px;
        `,
    },
    packLabelText: {
        weight: "bold",
    },
    savingsMessageText: {
        italic: true,
        size: "12px",
    },
    quoteMessage: {
        text: { weight: "bold" },
    },
});

const SectionWrapper = getStyledWrapper("section");

const ProductPrice = ({
    product: pricingData,
    currencySymbol,
    canSeePrices,
    showLabel = false,
    label,
    showUnitOfMeasure = true,
    showPack = true,
    showSavings = false,
    showSavingsAmount = false,
    showSavingsPercent = false,
    showInvalidPriceMessage = false,
    enableVat,
    vatPriceDisplay,
    showPricing,
    isCheckoutReviewAndSubmitPage,
    extendedStyles,
    showSalePriceLabel = true,
    session,
    showStandardPrice,
}: Props) => {
    if (!showPricing && !isCheckoutReviewAndSubmitPage) {
        return null;
    }
    const productContextModel = pricingData as ProductContextModel;
    const cartLineModel = pricingData as CartLineModel;
    const isAccountManager = session?.userRoles?.includes("AccountManager");
    const isEligibleForStandardCost =
        session?.userRoles?.includes("AccountManager") && session?.userRoles?.includes("CanViewStndCost");

    const [styles] = React.useState(() =>
        mergeToNew(getProductPriceStyles(isEligibleForStandardCost && showStandardPrice), extendedStyles),
    );

    const productNumber = "product" in pricingData ? pricingData.product.productNumber : pricingData.erpNumber;
    const mfg = "product" in pricingData ? pricingData.product.manufacturerItem : pricingData.manufacturerItem;
    const unitOfMeasureDescription =
        "product" in pricingData
            ? pricingData.product.unitOfMeasures?.find(o => o.unitOfMeasure === pricingData.productInfo.unitOfMeasure)
                  ?.description
            : pricingData.unitOfMeasureDescription;
    const unitOfMeasureDisplay =
        "product" in pricingData
            ? pricingData.product.unitOfMeasures?.find(o => o.unitOfMeasure === pricingData.productInfo.unitOfMeasure)
                  ?.unitOfMeasureDisplay
            : pricingData.unitOfMeasureDisplay;
    const quoteRequired = productContextModel.product
        ? productContextModel.product.quoteRequired
        : cartLineModel.quoteRequired;
    const qtyOrdered = productContextModel.product
        ? productContextModel.productInfo.qtyOrdered
        : cartLineModel.qtyOrdered;

    const pricing = "product" in pricingData ? pricingData.productInfo.pricing : pricingData.pricing;
    const allowZeroPricing =
        "product" in pricingData ? pricingData.product.allowZeroPricing : pricingData.allowZeroPricing;
    const isPromotionItem = "product" in pricingData ? false : pricingData.isPromotionItem;
    const isInvalidPrice =
        !quoteRequired &&
        !isPromotionItem &&
        !allowZeroPricing &&
        pricing?.unitNetPrice === 0 &&
        pricing?.unitRegularPrice === 0;

    const unitOfMeasure = unitOfMeasureDescription || unitOfMeasureDisplay;
    const showQtyPerBaseUnitOfMeasure =
        "baseUnitOfMeasure" in pricingData &&
        pricingData.unitOfMeasure !== pricingData.baseUnitOfMeasure &&
        pricingData.qtyPerBaseUnitOfMeasure > 0;
    const packDescription = "product" in pricingData ? pricingData.product.packDescription : undefined;
    // call for price change
    const INVALID_PRICE_ZERO = 0;
    const INVALID_PRICE_NOT_SET = 20000;
    const isUserAuthenticated = session?.isAuthenticated ?? false;

    const isInvalidPriceCheck = (price: number | undefined): boolean => {
        if (price === undefined || price === null || Number.isNaN(price)) {
            return true;
        }
        return price === INVALID_PRICE_ZERO || price === INVALID_PRICE_NOT_SET;
    };

    const quoteStyles = styles.quoteMessage || {};
    const priceStyles = styles.price || {};
    const regularPriceStyles = styles.regularPrice || {};
    const secondaryPriceStyles = styles.secondaryPrice || {};

    let rangePrice;
    let maxPrice;
    let minPrice;
    let maxPriceDisplay;
    let minPriceDisplay;
    if ("product" in pricingData) {
        if (pricingData.product.properties?.maxBasicListPrice && pricingData.product.properties?.minBasicListPrice) {
            maxPrice = +(pricingData.product.properties?.maxBasicListPrice ?? 0);
            minPrice = +(pricingData.product.properties?.minBasicListPrice ?? 0);

            maxPriceDisplay = pricingData.product.properties?.maxBasicListPriceDisplay ?? "0";
            minPriceDisplay = pricingData.product.properties?.minBasicListPriceDisplay ?? "0";

            if (maxPrice === minPrice) {
                rangePrice = maxPriceDisplay;
            } else {
                rangePrice = `${minPriceDisplay} - ${maxPriceDisplay}`;
            }
        }
    }

    const shouldShowCallForPrice = (() => {
        // Always check for invalid prices (0 or 20000) regardless of authentication
        if (!rangePrice && !isPromotionItem && isInvalidPriceCheck(pricing?.unitNetPrice)) {
            return true;
        }

        // For logged-out users, also check the callForPrice flag
        if (!isUserAuthenticated) {
            if ("product" in pricingData && pricingData?.product?.properties?.callForPrice === "true") {
                return true;
            }
        }

        return false;
    })();

    if (shouldShowCallForPrice) {
        return (
            <>
                <StyledWrapper {...styles.callForPriceWrapper} data-test-selector="callForPrice">
                    <StyledWrapper {...styles.callForPriceIcon}>
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M11.25 6C11.25 4.60761 10.6969 3.27226 9.71231 2.28769C8.72774 1.30312 7.39239 0.75 6 0.75C4.60761 0.75 3.27226 1.30312 2.28769 2.28769C1.30312 3.27226 0.75 4.60761 0.75 6C0.75 7.39239 1.30312 8.72774 2.28769 9.71231C3.27226 10.6969 4.60761 11.25 6 11.25C7.39239 11.25 8.72774 10.6969 9.71231 9.71231C10.6969 8.72774 11.25 7.39239 11.25 6ZM0 6C0 4.4087 0.632141 2.88258 1.75736 1.75736C2.88258 0.632141 4.4087 0 6 0C7.5913 0 9.11742 0.632141 10.2426 1.75736C11.3679 2.88258 12 4.4087 12 6C12 7.5913 11.3679 9.11742 10.2426 10.2426C9.11742 11.3679 7.5913 12 6 12C4.4087 12 2.88258 11.3679 1.75736 10.2426C0.632141 9.11742 0 7.5913 0 6ZM6.375 3.39141C6.7125 3.42422 7.05234 3.4875 7.38047 3.56953L7.74375 3.66094L7.56094 4.3875L7.19766 4.29609C7.05469 4.26094 6.59531 4.17656 6.44531 4.15312C5.99063 4.08516 5.59688 4.13672 5.32031 4.25625C4.89609 4.43672 4.69453 4.92656 5.12813 5.20312C5.4 5.37656 5.72812 5.46797 6.04219 5.55234C6.09844 5.56875 6.15469 5.58281 6.20859 5.59922C6.62344 5.71641 7.12031 5.85938 7.48125 6.10781C7.92656 6.4125 8.13281 6.9 8.03437 7.43203C7.94062 7.93828 7.59141 8.27578 7.14844 8.45391C6.91641 8.54766 6.65391 8.60156 6.37266 8.61563V9.5625H5.625V8.58047C5.32969 8.53359 4.85625 8.38125 4.54922 8.28281C4.41094 8.23828 4.27266 8.19609 4.13437 8.15156C4.09922 8.13984 4.06641 8.13047 4.03125 8.11875L4.25625 7.40391C4.28906 7.41328 4.31953 7.425 4.35234 7.43438C4.50469 7.48125 4.65703 7.53047 4.80937 7.57734C5.12578 7.67812 5.51719 7.80469 5.74453 7.83984C6.20859 7.90781 6.6 7.87031 6.86953 7.76016C7.31953 7.57734 7.48828 7.01953 7.05938 6.72656C6.77813 6.53438 6.43125 6.43828 6.10078 6.34687C6.04687 6.33281 5.99062 6.31641 5.93906 6.30234C5.53828 6.18984 5.07187 6.05625 4.72734 5.83594C4.5375 5.71406 4.35937 5.55234 4.24687 5.32734C4.13203 5.09766 4.10156 4.83984 4.15312 4.55859C4.23984 4.08047 4.59844 3.74766 5.02734 3.56484C5.21016 3.4875 5.41172 3.43359 5.62734 3.40078V2.4375H6.37734V3.39141H6.375Z"
                                fill="#FF8B36"
                            />
                        </svg>
                    </StyledWrapper>
                    <Typography {...styles.callforPriceText}>{siteMessage("Call_For_Price")}</Typography>
                </StyledWrapper>
            </>
        );
    }

    if (!canSeePrices) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <Typography {...priceStyles.signInText} data-test-selector="productPrice_PricingMessage">
                    {siteMessage("Pricing_SignInForPrice")}
                </Typography>
            </StyledWrapper>
        );
    }

    if ("product" in pricingData && pricingData.productInfo.failedToLoadPricing) {
        return (
            <SectionWrapper {...styles.wrapper}>
                <Typography {...priceStyles.errorText} data-test-selector="productPrice_PricingMessage">
                    {siteMessage("RealTimePricing_PriceLoadFailed")}
                </Typography>
            </SectionWrapper>
        );
    }

    if (quoteRequired) {
        return (
            <SectionWrapper {...styles.wrapper}>
                {showLabel && <Typography {...styles.priceLabelText}>{translate("Price")}</Typography>}
                <StyledWrapper {...styles.quoteMessageWrapper}>
                    <Typography {...quoteStyles.text} data-test-selector="quoteRequiredText">
                        {translate("Requires Quote")}
                    </Typography>
                    <Tooltip {...quoteStyles.tooltip} text={siteMessage("Rfq_TooltipMessage").toString()} />
                    <UnitOfMeasure
                        unitOfMeasure={unitOfMeasure}
                        showUnitOfMeasure={showUnitOfMeasure}
                        styles={styles.unitOfMeasure}
                    />
                </StyledWrapper>
            </SectionWrapper>
        );
    }

    const displayVatForPrice = vatPriceDisplay === "DisplayWithVat" || vatPriceDisplay === "DisplayWithAndWithoutVat";
    const showSecondaryPrice = enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat";
    const salePriceLabel = showSalePriceLabel
        ? productContextModel.product?.salePriceLabel || cartLineModel?.salePriceLabel || translate("Sale Price:")
        : "";

    return (
        <SectionWrapper {...styles.wrapper}>
            {showLabel && <Typography {...styles.priceLabelText}>{label || translate("Price")}</Typography>}
            <StyledWrapper {...styles.productPriceWrapper} data-test-selector="priceIncludeVat">
                {pricing && pricing?.actualPrice === 0 && rangePrice ? (
                    <Typography {...priceStyles.priceText} data-test-selector="productPrice_unitNetPrice">
                        {`${rangePrice}`}
                    </Typography>
                ) : (
                    <>
                        <StyledWrapper {...priceStyles.pricingContainer}>
                            <Price
                                currencySymbol={currencySymbol}
                                pricing={pricing}
                                qtyOrdered={qtyOrdered}
                                unitOfMeasure={unitOfMeasure}
                                showUnitOfMeasure={showUnitOfMeasure}
                                enableVat={enableVat}
                                displayWithVat={displayVatForPrice}
                                styles={priceStyles}
                                salePriceLabel={salePriceLabel}
                                mfg={mfg}
                                productNumber={productNumber}
                                showStandardPrice={showStandardPrice}
                                isAccountManager={isAccountManager}
                            />
                            {isEligibleForStandardCost && showStandardPrice && pricing && (
                                <PriceDetails
                                    productNumber={productNumber}
                                    mfg={mfg}
                                    pricing={pricing}
                                    qtyOrdered={qtyOrdered || 1}
                                    unitOfMeasure={unitOfMeasure}
                                />
                            )}
                            <RegularPrice
                                pricing={pricing}
                                qtyOrdered={qtyOrdered}
                                unitOfMeasure={unitOfMeasure}
                                showUnitOfMeasure={showUnitOfMeasure}
                                styles={regularPriceStyles}
                            />
                        </StyledWrapper>
                    </>
                )}
                {isInvalidPrice && showInvalidPriceMessage && (
                    <Typography as="p" {...styles.invalidPriceText} data-test-selector="productPrice_invalid">
                        {translate("Invalid Price")}
                    </Typography>
                )}
            </StyledWrapper>
            {showSecondaryPrice && (
                <StyledWrapper {...styles.secondaryPriceWrapper} data-test-selector="priceExcludeVat">
                    <Price
                        currencySymbol={currencySymbol}
                        pricing={pricing}
                        qtyOrdered={qtyOrdered}
                        unitOfMeasure={unitOfMeasure}
                        showUnitOfMeasure={showUnitOfMeasure}
                        enableVat={enableVat}
                        displayWithVat={false}
                        styles={secondaryPriceStyles}
                    />
                </StyledWrapper>
            )}
            {/* {showQtyPerBaseUnitOfMeasure && "baseUnitOfMeasure" in pricingData && (
                <Typography {...priceStyles.qtyPerBaseUnitOfMeasureText}>
                    {`${pricingData.qtyPerBaseUnitOfMeasure} ${pricingData.baseUnitOfMeasureDisplay} / ${unitOfMeasure}`}
                </Typography>
            )} */}
            {packDescription && showPack && (
                <StyledWrapper {...styles.packWrapper}>
                    <Typography {...styles.packLabelText}>{translate("Pack")}:&nbsp;</Typography>
                    <Typography {...styles.packDescriptionText}>{packDescription}</Typography>
                </StyledWrapper>
            )}
            {/* {pricing && showSavings && (
                <ProductPriceSavingsMessage
                    pricing={pricing}
                    qtyOrdered={qtyOrdered || 1}
                    showSavingsAmount={showSavingsAmount}
                    showSavingsPercent={showSavingsPercent}
                    currencySymbol={currencySymbol}
                    {...styles.savingsMessageText}
                />
            )} */}
        </SectionWrapper>
    );
};

interface PriceProps {
    currencySymbol: string;
    pricing: ProductPriceDto | null | undefined;
    qtyOrdered: number | null;
    unitOfMeasure?: string;
    showUnitOfMeasure?: boolean;
    enableVat: boolean;
    displayWithVat: boolean;
    styles: PriceStyles;
    salePriceLabel?: string;
    showStandardPrice?: boolean;
    isAccountManager?: boolean;
    productNumber?: string;
    mfg?: string;
}

const Price = ({
    currencySymbol,
    pricing,
    qtyOrdered,
    unitOfMeasure,
    showUnitOfMeasure = true,
    enableVat,
    displayWithVat,
    styles,
    salePriceLabel,
    isAccountManager,
}: PriceProps) => {
    let salePriceLabelPrefix = translate("Price");
    if (pricing && salePriceLabel) {
        const { price: unitNetPrice } = getUnitNetPrice(pricing, qtyOrdered || 1);
        const { price: unitListPrice } = getUnitListPrice(pricing, qtyOrdered || 1);
        if (unitNetPrice < unitListPrice && isAccountManager) {
            salePriceLabelPrefix = translate("Discounted price");
        } else if (unitNetPrice < unitListPrice) {
            salePriceLabelPrefix = `${salePriceLabel} `;
        }
    }

    return (
        <>
            {pricing ? (
                <StyledWrapper {...styles.pricingWrapper}>
                    <Typography {...styles.labelText}>{salePriceLabelPrefix}</Typography>
                    <StyledWrapper {...styles.priceLine}>
                        <Typography {...styles.priceText} data-test-selector="productPrice_unitNetPrice">
                            {enableVat && displayWithVat
                                ? getUnitRegularPriceWithVat(pricing, qtyOrdered || 1).priceDisplay
                                : getUnitNetPrice(pricing, qtyOrdered || 1).priceDisplay}
                        </Typography>
                        <UnitOfMeasure
                            unitOfMeasure={unitOfMeasure}
                            showUnitOfMeasure={showUnitOfMeasure}
                            styles={styles.unitOfMeasure}
                        />
                    </StyledWrapper>
                </StyledWrapper>
            ) : (
                <Typography {...styles.realTimeText} data-test-selector="productPrice_unitNetPrice_CurrencySymbol">
                    {currencySymbol}
                </Typography>
            )}

            {pricing && enableVat && (
                <Typography as="p" {...styles.vatLabelText}>
                    {displayWithVat ? `${translate("Inc. VAT")} (${pricing.vatRate}%)` : translate("Ex. VAT")}
                </Typography>
            )}
        </>
    );
};

interface RegularPriceProps {
    pricing: ProductPriceDto | null | undefined;
    qtyOrdered: number | null;
    unitOfMeasure?: string;
    showUnitOfMeasure?: boolean;
    styles: RegularPriceStyles;
}
const RegularPrice = ({ pricing, qtyOrdered, unitOfMeasure, showUnitOfMeasure = true, styles }: RegularPriceProps) => {
    let unitListPriceDisplay: string | undefined;

    if (pricing) {
        const { price: unitNetPrice } = getUnitNetPrice(pricing, qtyOrdered || 1);
        const { price: unitListPrice, priceDisplay } = getUnitListPrice(pricing, qtyOrdered || 1);
        const hasPriceSavings = unitNetPrice < unitListPrice;

        if (!hasPriceSavings) {
            return null;
        }

        unitListPriceDisplay = priceDisplay;
    }

    return (
        <>
            {pricing && (
                <StyledWrapper {...styles.regularPricewrapper}>
                    <Typography {...styles.labelText}>{translate("Regular price")}</Typography>
                    <StyledWrapper {...styles.priceLine}>
                        <Typography {...styles.priceText}>{unitListPriceDisplay}</Typography>
                        <UnitOfMeasure
                            unitOfMeasure={unitOfMeasure}
                            showUnitOfMeasure={showUnitOfMeasure}
                            styles={styles.unitOfMeasure}
                        />
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </>
    );
};

interface UnitOfMeasureProps {
    unitOfMeasure?: string;
    showUnitOfMeasure?: boolean;
    styles?: UnitOfMeasureStyles;
}

const UnitOfMeasure = ({ unitOfMeasure, showUnitOfMeasure = true, styles }: UnitOfMeasureProps) => {
    return unitOfMeasure && showUnitOfMeasure ? (
        <Typography {...styles?.unitOfMeasureText} data-test-selector="productPrice_unitOfMeasureLabel">
            &nbsp;({unitOfMeasure})
        </Typography>
    ) : null;
};

export default connect(mapStateToProps)(ProductPrice);
